import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { IAndITimePeriodsValue } from "@/config/date";
import { formatAdaptive } from "@/utils/number";
import { Flame } from "lucide-react";
import { FC } from "react";
import { observer } from "mobx-react-lite";
import { ErrorContent } from "../ErrorContent/ErrorContent";
import { NotFoundContent } from "../NotFoundContent";
import { useIAndIWorstAffected } from "../useIAndISummaryData";

const WORST_AFFECTED_SIZE = 5;

const WorstAffectedSkeleton: FC = () => (
	<>
		<div className="flex flex-col items-center gap-4">
			<Skeleton className="h-12 w-64 rounded-md bg-blue-50 mt-2" />
			<Skeleton className="h-4 w-64 rounded-md bg-blue-50" />
			<div className="w-full flex flex-col gap-2">
				{Array.from({ length: WORST_AFFECTED_SIZE }).map((_, index) => (
					<Skeleton key={index} className="h-5 rounded-md bg-blue-50 w-full" />
				))}
			</div>
		</div>
	</>
);

export const WorstAffectedCard = observer(() => {
	const timePeriod: IAndITimePeriodsValue = "last30d";
	const {
		loading,
		loaded,
		error,
		items: worstAffectedStructures,
	} = useIAndIWorstAffected(timePeriod, WORST_AFFECTED_SIZE);
	const isNoData = loaded && !error && worstAffectedStructures.length === 0;

	return (
		<Card className="shadow-sm border border-muted rounded-xl flex flex-col h-full drop-shadow-sm">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold flex items-center gap-2">
					<Flame className="h-5 w-5 text-orange-500" fill="currentColor" />
					Worst Affected
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-1 flex flex-col justify-center">
				{loading ? (
					<WorstAffectedSkeleton />
				) : isNoData ? (
					<NotFoundContent message="No worst affected structures identified" />
				) : error ? (
					<ErrorContent
						error={error}
						notFoundMessage="No worst affected structures identified"
						message="Unable to load worst affected structures"
					/>
				) : (
					<div className="space-y-1.5 text-xs">
						{worstAffectedStructures.map((iAndI, index) => (
							<div key={iAndI.structure.id} className="flex items-center justify-between border-b pb-1">
								<span className="font-medium text-foreground truncate">
									{index + 1}. {iAndI.structure.name}
								</span>
								<span className="text-muted-foreground font-mono tabular-nums">
									{formatAdaptive(iAndI.total?.flowPerLength?.value ?? 0)}{" "}
									{iAndI.total?.flowPerLength?.unit.symbol}
								</span>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
});
